import type { VideoObject } from "schema-dts";

export type PublisherData = {
  name: string;
  logoUrl?: string;
};

export type VideoObjectData = {
  name?: string;
  description?: string;
  thumbnailUrl?: string;
  thumbnailAlt?: string;
  contentUrl?: string;
  uploadDate?: string;
  duration?: string;
  ineligibleRegion?: string;
  publisher?: PublisherData;
};

export function isValidIsoDuration(value?: string): boolean {
  if (!value) return false;
  return /^P(?:\d+Y)?(?:\d+M)?(?:\d+W)?(?:\d+D)?(?:T(?:\d+H)?(?:\d+M)?(?:\d+S)?)?$/.test(value) && value !== "P";
}

const DEFAULT_DURATION = "PT30S";

/**
 * Attempts to convert common duration formats to ISO 8601.
 * Supports: "H:MM:SS", "M:SS", plain seconds number, "Xh Ym Zs", "XhYmZs".
 * Falls back to DEFAULT_DURATION ("PT30S") if the value cannot be parsed.
 */
export function normalizeDuration(value?: string): string {
  if (!value) return DEFAULT_DURATION;
  if (isValidIsoDuration(value)) return value;

  const v = value.trim();

  // H:MM:SS or M:SS
  const colonMatch = v.match(/^(?:(\d+):)?(\d+):(\d{2})$/);
  if (colonMatch) {
    const hours = colonMatch[1] ? parseInt(colonMatch[1], 10) : 0;
    const minutes = parseInt(colonMatch[2] ?? "0", 10);
    const seconds = parseInt(colonMatch[3] ?? "0", 10);
    const parts = `${hours ? `${hours}H` : ""}${minutes ? `${minutes}M` : ""}${seconds ? `${seconds}S` : ""}`;
    return parts ? `PT${parts}` : DEFAULT_DURATION;
  }

  // "Xh Ym Zs" or "XhYmZs" (case-insensitive)
  const unitMatch = v.match(/^(?:(\d+)\s*h)?(?:\s*(\d+)\s*m)?(?:\s*(\d+)\s*s)?$/i);
  if (unitMatch && (unitMatch[1] || unitMatch[2] || unitMatch[3])) {
    const h = unitMatch[1] ? `${unitMatch[1]}H` : "";
    const m = unitMatch[2] ? `${unitMatch[2]}M` : "";
    const s = unitMatch[3] ? `${unitMatch[3]}S` : "";
    return `PT${h}${m}${s}`;
  }

  // Plain number → seconds
  const plainSeconds = parseFloat(v);
  if (!isNaN(plainSeconds) && plainSeconds > 0) {
    return `PT${Math.round(plainSeconds)}S`;
  }

  return DEFAULT_DURATION;
}

export function getYouTubeEmbedUrl(watchUrl?: string): string | undefined {
  if (!watchUrl) return undefined;
  try {
    const { hostname, searchParams } = new URL(watchUrl);
    if (!hostname.endsWith("youtube.com")) return undefined;
    const videoId = searchParams.get("v");
    return videoId ? `https://www.youtube.com/embed/${videoId}` : undefined;
  } catch {
    return undefined;
  }
}

export function normalizeUploadDate(value?: string): string | undefined {
  if (!value) return undefined;

  // Already a valid ISO 8601 date or datetime string
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
    const d = new Date(value);
    return isNaN(d.getTime()) ? undefined : d.toISOString();
  }

  // Builder timestamp in milliseconds (numeric string)
  const timestamp = Number(value);
  if (!isNaN(timestamp) && timestamp > 0) {
    return new Date(timestamp).toISOString();
  }

  // Fallback: try native Date parsing (covers formats like Date.toString() output)
  const parsed = new Date(value);
  if (!isNaN(parsed.getTime())) {
    return parsed.toISOString();
  }

  return undefined;
}

export function buildVideoObjectData({
  name,
  description,
  thumbnailUrl,
  thumbnailAlt,
  contentUrl,
  uploadDate,
  duration,
  ineligibleRegion,
  publisher,
}: VideoObjectData): VideoObject | null {
  if (!name || !description) {
    return null;
  }

  const normalizedUploadDate = normalizeUploadDate(uploadDate);

  const embedUrl = getYouTubeEmbedUrl(contentUrl);
  const validDuration = isValidIsoDuration(duration) ? duration : undefined;
  const regions = ineligibleRegion
    ? ineligibleRegion
        .split(",")
        .map((r) => r.trim())
        .filter(Boolean)
    : [];

  return {
    "@type": "VideoObject",
    name,
    description,
    ...(thumbnailUrl ? { thumbnailUrl } : {}),
    ...(thumbnailUrl || thumbnailAlt
      ? {
          thumbnail: {
            "@type": "ImageObject" as const,
            ...(thumbnailUrl ? { url: thumbnailUrl } : {}),
            ...(thumbnailAlt ? { caption: thumbnailAlt } : {}),
          },
        }
      : {}),
    ...(normalizedUploadDate ? { uploadDate: normalizedUploadDate } : {}),
    ...(contentUrl ? { contentUrl } : {}),
    ...(embedUrl ? { embedUrl } : {}),
    ...(validDuration ? { duration: validDuration } : {}),
    ...(publisher
      ? {
          publisher: {
            "@type": "Organization",
            name: publisher.name,
            ...(publisher.logoUrl ? { logo: { "@type": "ImageObject", url: publisher.logoUrl } } : {}),
          },
        }
      : {}),
    ...(regions.length > 0
      ? {
          ineligibleRegion: regions.map((regionCode) => ({
            "@type": "Country",
            name: regionCode,
          })),
        }
      : {}),
  };
}
